import React, { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Form, Input, Modal, Segmented, Space, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CompressOutlined,
  ExpandOutlined,
  ExportOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { formatMoney } from '@/utils/format';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { EpcWbsLibraryTreeWorkbench } from './EpcWbsLibraryTreeWorkbench';
import { EpcWbsLibraryNodeInspector } from './EpcWbsLibraryNodeInspector';
import { EpcWbsLibraryPackageOverview } from './EpcWbsLibraryPackageOverview';
import { EpcWbsLibraryShortcutsModal } from './EpcWbsLibraryShortcutsModal';
import styles from '@/pages/epc/libraries/EpcWbsLibraryWorkspace.module.css';

const { Text, Title } = Typography;

export interface EpcWbsLibraryPackageWorkbenchProps {
  libraryId: string;
  libraryCode?: string;
  packageId: string;
  packageCode?: string;
  packageName?: string;
  nodes: any[];
  nodesLoading?: boolean;
  selectedNodeId?: string;
  nodeSearch: string;
  onSelectNode: (id: string | undefined) => void;
  onBack: () => void;
  onNodesChanged: () => void;
  readOnly?: boolean;
  focusMode?: boolean;
  onToggleFocus?: () => void;
  onPackageDuplicated?: (newPackageId: string) => void;
}

export const EpcWbsLibraryPackageWorkbench: React.FC<EpcWbsLibraryPackageWorkbenchProps> = ({
  libraryId,
  libraryCode,
  packageId,
  packageCode,
  packageName,
  nodes,
  nodesLoading,
  selectedNodeId,
  nodeSearch,
  onSelectNode,
  onBack,
  onNodesChanged,
  readOnly,
  focusMode,
  onToggleFocus,
  onPackageDuplicated,
}) => {
  const qc = useQueryClient();
  const [inspectorTab, setInspectorTab] = useState<'overview' | 'node'>('node');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupForm] = Form.useForm();
  const readinessQ = useQuery({
    queryKey: ['epc-wbs-lib-readiness', packageId],
    queryFn: () => epcWbsLibraryService.getPackageReadiness(packageId),
    enabled: !!packageId,
  });

  const firstPackageNodeId = useMemo(() => {
    const pkg = nodes.find((n) => String(n.layer_code) === 'PACKAGE');
    return pkg ? String(pkg.id) : undefined;
  }, [nodes]);

  useEffect(() => {
    if (!nodesLoading && nodes.length && !selectedNodeId && firstPackageNodeId) {
      onSelectNode(firstPackageNodeId);
    }
  }, [nodesLoading, nodes.length, selectedNodeId, firstPackageNodeId, onSelectNode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        setShortcutsOpen(true);
      }
      if (focusMode && e.key === 'Escape') onToggleFocus?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusMode, onToggleFocus]);

  const exportM = useMutation({
    mutationFn: () => epcWbsLibraryService.exportPackage(packageId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${packageCode || 'package'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const duplicateM = useMutation({
    mutationFn: (v: { code: string; name: string }) =>
      epcWbsLibraryService.duplicatePackage(packageId, v),
    onSuccess: (data: any) => {
      message.success('Package duplicated');
      setDupOpen(false);
      dupForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages', libraryId] });
      if (data?.id) onPackageDuplicated?.(String(data.id));
    },
    onError: (e: Error) => message.error(e.message),
  });

  const selectNodeByCode = (code: string) => {
    const node = nodes.find((n) => String(n.code) === code);
    if (node) {
      setInspectorTab('node');
      onSelectNode(String(node.id));
    }
  };

  const selectedNode = nodes.find((n) => String(n.id) === selectedNodeId);

  return (
    <div className={styles.packageShell}>
      <div className={styles.packageContextBar}>
        <Space wrap align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
            Packages
          </Button>
          <Breadcrumb
            items={[
              { title: libraryCode || 'Catalogue' },
              { title: packageCode || 'Package' },
            ]}
          />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {packageName}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {packageCode} · {nodes.length} WBS nodes
            </Text>
          </div>
        </Space>
        <Space wrap>
          {readinessQ.data ? (
            <Tag
              icon={
                readinessQ.data.isPlugReady ? <CheckCircleOutlined /> : <WarningOutlined />
              }
              color={readinessQ.data.isPlugReady ? 'success' : 'warning'}
            >
              {readinessQ.data.isPlugReady ? 'Plug-ready' : 'Needs CBS / structure fix'}
            </Tag>
          ) : null}
          {readinessQ.data ? (
            <Text type="secondary">
              Rollup{' '}
              {formatMoney(readinessQ.data.rolledUpDefaultCost, {
                display: 'plain',
                maximumFractionDigits: 0,
              })}
            </Text>
          ) : null}
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setShortcutsOpen(true)}
          >
            Shortcuts
          </Button>
          <Button
            icon={focusMode ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={onToggleFocus}
          >
            {focusMode ? 'Exit focus' : 'Focus mode'}
          </Button>
          <Button
            icon={<ExportOutlined />}
            loading={exportM.isPending}
            onClick={() => exportM.mutate()}
          >
            Export
          </Button>
        </Space>
      </div>

      <div className={styles.packageSplit}>
        <section className={styles.treePane}>
          <div className={styles.treePaneHeader}>WBS structure</div>
          <div className={styles.treePaneBody}>
            <EpcWbsLibraryTreeWorkbench
              packageId={packageId}
              libraryId={libraryId}
              nodes={nodes}
              loading={nodesLoading}
              readOnly={readOnly}
              selectedNodeId={selectedNodeId}
              nodeSearch={nodeSearch}
              onSelectNode={onSelectNode}
              onChanged={onNodesChanged}
              onShowShortcuts={() => setShortcutsOpen(true)}
            />
          </div>
        </section>

        <section className={styles.inspectorPane}>
          <div className={styles.inspectorPaneHeader}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
              <span>
                {inspectorTab === 'overview'
                  ? 'Package overview'
                  : selectedNode
                    ? `${selectedNode.code} — ${selectedNode.name}`
                    : 'Select a node'}
              </span>
              <Segmented
                size="small"
                value={inspectorTab}
                onChange={(v) => setInspectorTab(v as 'overview' | 'node')}
                options={[
                  { label: 'Overview', value: 'overview' },
                  { label: 'Node', value: 'node' },
                ]}
              />
            </Space>
          </div>
          <div className={styles.inspectorPaneBody}>
            {inspectorTab === 'overview' ? (
              <EpcWbsLibraryPackageOverview
                packageId={packageId}
                packageCode={packageCode}
                readOnly={readOnly}
                onSelectNode={(id) => {
                  setInspectorTab('node');
                  onSelectNode(id);
                }}
                onSelectNodeByCode={selectNodeByCode}
                onDuplicate={
                  readOnly
                    ? undefined
                    : () => {
                        dupForm.setFieldsValue({
                          code: `${packageCode || 'PKG'}-COPY`,
                          name: `${packageName || 'Package'} (copy)`,
                        });
                        setDupOpen(true);
                      }
                }
              />
            ) : (
              <EpcWbsLibraryNodeInspector
                packageId={packageId}
                packageCode={packageCode}
                nodes={nodes}
                selectedNodeId={selectedNodeId}
                readOnly={readOnly}
                compact
              />
            )}
          </div>
        </section>
      </div>

      <EpcWbsLibraryShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <Modal
        title="Duplicate package"
        open={dupOpen}
        onCancel={() => setDupOpen(false)}
        onOk={() => dupForm.submit()}
        confirmLoading={duplicateM.isPending}
      >
        <Form form={dupForm} layout="vertical" onFinish={(v) => duplicateM.mutate(v)}>
          <Form.Item name="code" label="New code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="New name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
